import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpMethod } from '@vision/ui/enums';
import type { ApiResponse } from '@vision/ui/interfaces';
import { buildEndpointWithQuery, createHttpExtraOptions } from '@vision/ui/utils';
import { axiosBaseQuery } from '../base.service';
import type { ChannelProviderDto, GetChannelProvidersArgs } from './channel-campaigns.api.types';
import { fromApiChannelProviders, toApiChannelProvidersUrl } from './channel-campaigns.mapper';
import type {
  AssignChannelFlowArgs,
  ChannelCampaignListResponseDto,
  ChannelDashboardResponseDto,
  ChannelListResponseDto,
  GetChannelDashboardArgs,
  GetChannelFlowPagesArgs,
  GetChannelsArgs,
  GetChannelsByTypeArgs,
  SearchChannelFlowsArgs,
  SearchChannelFlowsResponseDto,
  SearchChannelsArgs,
  SearchChannelsResponseDto,
} from './channel-lists.api.types';
import {
  fromApiChannelDashboard,
  fromApiChannelFlowPage,
  fromApiChannelFlowSearch,
  fromApiChannelList,
  fromApiChannelsByType,
  fromApiSearchChannels,
  toApiAssignChannelFlow,
  toApiChannelsByTypeUrl,
  toApiGetChannelFlowPage,
  toApiGetChannels,
  toApiSearchChannels,
} from './channel-lists.mapper';
import type {
  ChannelStatisticsResponseDto,
  CreateEmailSharingArgs,
  CreateLinkSharingArgs,
  CreateMessageSharingArgs,
  ExportCatiCallsArgs,
  GetCatiImportTemplateArgs,
  GetChannelStatisticsArgs,
  GetIncomingEmailAddressArgs,
  GetLinkChannelSettingsArgs,
  GetMessageCampaignSmsProviderArgs,
  ImportCatiCallsArgs,
  IncomingEmailAddressResponseDto,
  LinkChannelSettingsDto,
  MessageCampaignSmsProviderResponseDto,
  ShortenChannelLinkArgs,
  ShortenChannelLinkResponseDto,
  UpdateLinkChannelSettingsArgs,
} from './channel-publishing.api.types';
import {
  fromApiChannelStatistics,
  fromApiLinkChannelSettings,
  toApiCatiImportTemplate,
  toApiChannelStatistics,
  toApiChannelStatisticsUrl,
  toApiCreateEmailSharing,
  toApiCreateLinkSharing,
  toApiCreateMessageSharing,
  toApiExportCatiCalls,
  toApiImportCatiCalls,
  toApiIncomingEmailAddress,
  toApiLinkChannelSettings,
  toApiMessageCampaignSmsProviderUrl,
  toApiMessageSharingUrl,
} from './channel-publishing.mapper';
import type { ChannelDetailResponseDto, GetChannelDetailArgs, UpsertChannelArgs } from './channels.api.types';
import {
  fromApiChannelDetail,
  toApiChannelDetailUrl,
  toApiUpsertChannel,
  toApiUpsertChannelUrl,
} from './channels.mapper';
import type {
  ChannelCampaignListResult,
  ChannelDashboard,
  ChannelFlowSearchItem,
  ChannelFlowSearchPage,
  ChannelListResult,
  ChannelProvider,
  ChannelSearchResult,
  ChannelStatistics,
  LinkChannelSettings,
  SavedChannel,
} from './types';

// Channels landing and layout designer endpoints only.
export const ChannelsService = createApi({
  reducerPath: 'ChannelsService',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['ChannelDetail', 'ChannelsByType'],
  endpoints: (builder) => ({
    getChannelDashboard: builder.query<ChannelDashboard, GetChannelDashboardArgs>({
      query: ({ accountId }) => ({
        url: `/v2/accounts/${accountId}/channels/dashboard`,
        method: HttpMethod.GET,
      }),
      transformResponse: (response: ChannelDashboardResponseDto) => fromApiChannelDashboard(response),
    }),
    getChannels: builder.query<ChannelListResult, GetChannelsArgs>({
      query: (args) => ({
        url: `/v2/accounts/${args.accountId}/channels/index${args.query ? `?${args.query}` : ''}`,
        method: HttpMethod.GET,
        params: toApiGetChannels(args),
      }),
      transformResponse: (response: ChannelListResponseDto) => fromApiChannelList(response),
    }),
    getChannelsByType: builder.query<ChannelCampaignListResult, GetChannelsByTypeArgs>({
      query: (args) => ({
        url: buildEndpointWithQuery(toApiChannelsByTypeUrl(args), args.query),
        method: HttpMethod.GET,
      }),
      providesTags: ['ChannelsByType'],
      transformResponse: (response: ChannelCampaignListResponseDto, _meta, { channelType }) =>
        fromApiChannelsByType(response, channelType),
    }),
    // No v2 endpoint has the funnel counts; the legacy channel lists' statistics use these.
    getChannelStatistics: builder.query<ChannelStatistics, GetChannelStatisticsArgs>({
      query: (args) => ({
        url: toApiChannelStatisticsUrl(args),
        method: HttpMethod.GET,
        params: toApiChannelStatistics(args),
      }),
      transformResponse: (response: ChannelStatisticsResponseDto) => fromApiChannelStatistics(response),
    }),
    // Publishing settings have no v2 endpoint; the legacy Link channel deploy modal uses these.
    getLinkChannelSettings: builder.query<LinkChannelSettings, GetLinkChannelSettingsArgs>({
      query: ({ channelId }) => ({
        url: `/v1/link_channels/${channelId}`,
        method: HttpMethod.GET,
      }),
      transformResponse: (response: LinkChannelSettingsDto) => fromApiLinkChannelSettings(response),
    }),
    // Invalidates nothing: the modal closes after a save and fetches the settings again when it reopens, and a
    // refetch here would turn the closing modal into its loader.
    updateLinkChannelSettings: builder.mutation<void, UpdateLinkChannelSettingsArgs>({
      query: ({ channelId, settings }) => ({
        url: `/v1/link_channels/${channelId}`,
        method: HttpMethod.POST,
        data: toApiLinkChannelSettings(settings),
        // The modal shows its own success message.
        extraOptions: createHttpExtraOptions({ notification: { autoHandleSuccess: false } }),
      }),
    }),
    // The API rejects URLs outside the account's survey domains.
    shortenChannelLink: builder.mutation<string, ShortenChannelLinkArgs>({
      query: ({ channelId, url }) => ({
        url: `/v1/nodes/${channelId}/shorten_link`,
        method: HttpMethod.GET,
        params: { url },
      }),
      transformResponse: (response: ShortenChannelLinkResponseDto) => response.short_url,
    }),
    // The API prepares the links in the background and emails the file to the user.
    createLinkSharing: builder.mutation<void, CreateLinkSharingArgs>({
      query: (args) => ({
        url: `/v1/link_campaigns/${args.campaignId}/link_sharings`,
        method: HttpMethod.POST,
        data: toApiCreateLinkSharing(args),
        // The modal shows its own success message.
        extraOptions: createHttpExtraOptions({ notification: { autoHandleSuccess: false } }),
      }),
    }),
    // The API queues the emails, so success only confirms the request.
    createEmailSharing: builder.mutation<void, CreateEmailSharingArgs>({
      query: (args) => ({
        url: `/v1/email_campaigns/${args.campaignId}/email_sharings`,
        method: HttpMethod.POST,
        data: toApiCreateEmailSharing(args),
        // The modal shows its own success message.
        extraOptions: createHttpExtraOptions({ notification: { autoHandleSuccess: false } }),
      }),
    }),
    // The API queues the messages, so success only confirms the request.
    createMessageSharing: builder.mutation<void, CreateMessageSharingArgs>({
      query: (args) => ({
        url: toApiMessageSharingUrl(args),
        method: HttpMethod.POST,
        data: toApiCreateMessageSharing(args),
        // The modal shows its own messages, as some errors need explaining.
        extraOptions: createHttpExtraOptions({ notification: { autoHandleError: false, autoHandleSuccess: false } }),
      }),
    }),
    // Its type sets the phone number format; it is null without a provider.
    getMessageCampaignSmsProvider: builder.query<string | null, GetMessageCampaignSmsProviderArgs>({
      query: (args) => ({
        url: toApiMessageCampaignSmsProviderUrl(args),
        method: HttpMethod.GET,
      }),
      transformResponse: (response: MessageCampaignSmsProviderResponseDto) => response.sms_provider_type,
    }),
    // The API derives the address from the channel and the flow and stores nothing, so the POST is cached per flow.
    getIncomingEmailAddress: builder.query<string, GetIncomingEmailAddressArgs>({
      query: (args) => ({
        url: '/v1/email_campaigns/generate_incoming_email_address',
        method: HttpMethod.POST,
        data: toApiIncomingEmailAddress(args),
        // Showing the address is the result; a POST would otherwise announce a success.
        extraOptions: createHttpExtraOptions({ notification: { autoHandleSuccess: false } }),
      }),
      transformResponse: (response: IncomingEmailAddressResponseDto) => response.incoming_mail,
    }),
    // CATI exports and imports have no v2 endpoint; the legacy CATI channel list uses these.
    exportCatiCalls: builder.mutation<void, ExportCatiCallsArgs>({
      query: (args) => ({
        url: '/v1/cati/export',
        method: HttpMethod.POST,
        data: toApiExportCatiCalls(args),
        // The list shows its own success message, which names the address the file goes to.
        extraOptions: createHttpExtraOptions({ notification: { autoHandleSuccess: false } }),
      }),
    }),
    // A mutation rather than a query: each download asks for a fresh file, which the browser saves.
    getCatiImportTemplate: builder.mutation<Blob, GetCatiImportTemplateArgs>({
      query: (args) => ({
        url: '/v1/cati/export_template',
        method: HttpMethod.POST,
        data: toApiCatiImportTemplate(args),
        responseType: 'blob',
        // The downloaded file is the result.
        extraOptions: createHttpExtraOptions({ notification: { autoHandleSuccess: false } }),
      }),
    }),
    importCatiCalls: builder.mutation<void, ImportCatiCallsArgs>({
      query: (args) => ({
        url: '/v1/cati/import',
        method: HttpMethod.POST,
        data: toApiImportCatiCalls(args),
        // The modal shows its own success message.
        extraOptions: createHttpExtraOptions({ notification: { autoHandleSuccess: false } }),
      }),
    }),
    // No v2 endpoint assigns a flow; the legacy channel lists use this one.
    assignChannelFlow: builder.mutation<void, AssignChannelFlowArgs>({
      query: (args) => ({
        url: `/v1/nodes/${args.channelId}/assign_flow`,
        method: HttpMethod.POST,
        data: toApiAssignChannelFlow(args),
        // The flow select shows its own success message.
        extraOptions: createHttpExtraOptions({ notification: { autoHandleSuccess: false } }),
      }),
      // The type lists' publishing depends on the flow (whether one is set, its kind and the stable link).
      invalidatesTags: (_result, error) => (error ? [] : ['ChannelsByType']),
    }),
    getChannelDetail: builder.query<SavedChannel, GetChannelDetailArgs>({
      query: (args) => ({
        url: toApiChannelDetailUrl(args),
        method: HttpMethod.GET,
      }),
      // Theme writes change the assigned theme outside this service, so each visit loads the saved channel anew.
      keepUnusedDataFor: 0,
      providesTags: ['ChannelDetail'],
      transformResponse: (response: ChannelDetailResponseDto, _meta, { channelType }) =>
        fromApiChannelDetail(response, channelType),
    }),
    // The account's providers a channel can choose from.
    getChannelProviders: builder.query<ChannelProvider[], GetChannelProvidersArgs>({
      query: (args) => ({
        url: toApiChannelProvidersUrl(args),
        method: HttpMethod.GET,
      }),
      transformResponse: (response: ChannelProviderDto[]) => fromApiChannelProviders(response),
    }),
    // Creates the channel without a channel id and saves only the changed values with one.
    upsertChannel: builder.mutation<SavedChannel, UpsertChannelArgs>({
      query: (args) => {
        const message = 'values' in args ? 'createChannel' : 'updateChannel';

        return {
          url: toApiUpsertChannelUrl(args),
          method: HttpMethod.POST,
          data: toApiUpsertChannel(args),
          extraOptions: createHttpExtraOptions({
            notification: {
              autoHandleSuccess: args.notifySuccess !== false,
              translationMessageKey: `http.message.channel.${message}.{{status}}`,
            },
          }),
        };
      },
      // The wizard hydrates once per channel, so the refetched detail only serves the next visit.
      invalidatesTags: (_result, error) => (error ? [] : ['ChannelDetail', 'ChannelsByType']),
      transformResponse: (response: ChannelDetailResponseDto, _meta, { channelType }) =>
        fromApiChannelDetail(response, channelType),
    }),
    searchChannels: builder.query<ChannelSearchResult[], SearchChannelsArgs>({
      query: (args) => ({
        url: `/v2/accounts/${args.accountId}/channels/search`,
        method: HttpMethod.GET,
        params: toApiSearchChannels(args),
      }),
      transformResponse: (response: SearchChannelsResponseDto) => fromApiSearchChannels(response),
    }),
    // Paged for VLazyLoadSelect, which builds `query`.
    searchChannelFlows: builder.query<ApiResponse<ChannelFlowSearchItem[]>, SearchChannelFlowsArgs>({
      query: ({ accountId, query }) => ({
        url: buildEndpointWithQuery(`/v2/accounts/${accountId}/channels/flow_search`, query),
        method: HttpMethod.GET,
      }),
      transformResponse: (response: SearchChannelFlowsResponseDto) => fromApiChannelFlowSearch(response),
    }),
    getChannelFlowPages: builder.infiniteQuery<ChannelFlowSearchPage, GetChannelFlowPagesArgs, number>({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
      },
      query: ({ queryArg, pageParam }) => ({
        url: `/v2/accounts/${queryArg.accountId}/channels/flow_search`,
        method: HttpMethod.GET,
        params: toApiGetChannelFlowPage(queryArg, pageParam),
      }),
      transformResponse: (response: SearchChannelFlowsResponseDto) => fromApiChannelFlowPage(response),
    }),
  }),
});

export const {
  useAssignChannelFlowMutation,
  useCreateEmailSharingMutation,
  useCreateMessageSharingMutation,
  useGetMessageCampaignSmsProviderQuery,
  useCreateLinkSharingMutation,
  useExportCatiCallsMutation,
  useGetCatiImportTemplateMutation,
  useGetChannelDashboardQuery,
  useGetChannelDetailQuery,
  useGetChannelFlowPagesInfiniteQuery,
  useGetChannelProvidersQuery,
  useGetChannelStatisticsQuery,
  useGetIncomingEmailAddressQuery,
  useGetLinkChannelSettingsQuery,
  useImportCatiCallsMutation,
  useLazyGetChannelsByTypeQuery,
  useLazyGetChannelsQuery,
  useLazySearchChannelFlowsQuery,
  useLazySearchChannelsQuery,
  useShortenChannelLinkMutation,
  useUpdateLinkChannelSettingsMutation,
  useUpsertChannelMutation,
} = ChannelsService;
